import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { EnumEntityReferenceType, NoteManyQuery } from "gql/generated";
import _ from "lodash";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNotesContext } from "shared/contextProviders";
import { DATE_PATTERN_SIMPLE } from "shared/utils";
import { useAppContext } from "useAppContext";

type Filter =
  | "archived"
  | "myNotes"
  | "people"
  | "operators"
  | "lists"
  | "widgets";

export type TimelineNoteItem = {
  _id: string;
  groupByDate: string;
  note: NoteManyQuery["noteMany"][0];
};

export type NoteTimelineGroup = {
  groupByDate: string;
  items: TimelineNoteItem[];
};

const ALL_ENTITY_FILTERS: Filter[] = [
  "people",
  "operators",
  "lists",
  "widgets",
];

export const useGlobalNotesModal = () => {
  const { dbUser } = useAppContext();
  const {
    loadingLinkedToEntities,
    linkedToEntities,
    notes,
    loading,
    error,
    initialLoad,
  } = useNotesContext();

  const [filteredNotes, setFilteredNotes] = useState<NoteManyQuery["noteMany"]>(
    notes ? notes.filter(n => !n.isArchived) : [],
  );
  const [filtersOn, setFiltersOn] = useState<Filter[]>([]);
  const [search, setSearch] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);

  // When the initial notes are loaded, we need to filter out the archived notes
  // And set them to the filteredNotes state
  useEffect(() => {
    if (notes) {
      setFilteredNotes(notes ? notes.filter(n => !n.isArchived) : []);
    }
  }, [loading, notes]);

  if (error) {
    throw new Error(error.message);
  }

  /** Apply search string to list (expensive). Used by debounced effect and when notes/filters change. */
  const applySearchToNotes = useCallback(
    (searchString: string) => {
      const searchQuery = searchString.toLowerCase();
      const isArchivedFilterOn = filtersOn.includes("archived");
      const isMyNotesFilterOn = filtersOn.includes("myNotes");
      const currentEntityFilter = getCurrentEntityFilter(filtersOn);
      if (searchQuery.length === 0) {
        setFilteredNotes(
          getNotesFilteredByEntityType(
            getNotesFilteredByMyNotes(
              getNotesFilteredByArchived(notes, isArchivedFilterOn),
              isMyNotesFilterOn,
            ),
            currentEntityFilter,
          ),
        );
      } else {
        const filteredNotesBySearchQuery = getNotesFilteredByEntityType(
          getNotesFilteredByMyNotes(
            getNotesFilteredByArchived(
              (notes || []).filter(
                n =>
                  n.author?.firstName.toLowerCase().includes(searchQuery) ||
                  n.author?.lastName.toLowerCase().includes(searchQuery) ||
                  n.message.toLowerCase().includes(searchQuery),
              ),
              isArchivedFilterOn,
            ),
            isMyNotesFilterOn,
          ),
          currentEntityFilter,
        );
        setFilteredNotes(filteredNotesBySearchQuery);
      }
    },
    [notes, filtersOn, linkedToEntities],
  );

  // Input updates immediately; list filters after typing pauses so the UI stays responsive.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SEARCH_DEBOUNCE_MS = 200;
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      applySearchToNotes(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, applySearchToNotes]);

  /** Updates search input only; list is updated by debounced effect above. */
  const handleSearch = useCallback((searchString: string) => {
    setSearch(searchString);
  }, []);

  /**
   * Toggles the archived notes filter on/off
   *
   * Logic:
   * - When enabling archived filter, automatically removes "myNotes" filter (mutually exclusive)
   * - When disabling archived filter, shows only non-archived notes
   * - Always applies the entity filters (business rule)
   * - Clears search to avoid confusion with filtered results
   * - Updates both filter state and filtered notes immediately
   */
  const handleToggleArchivedNotes = () => {
    setSearch("");
    const isArchivedFilterOn = filtersOn.includes("archived");
    setFiltersOn(
      prev =>
        isArchivedFilterOn
          ? prev.filter(f => f !== "archived")
          : [...prev.filter(f => f !== "myNotes"), "archived"], // When adding archived filter, remove myNotes filter
    );
    const newNotes = getNotesFilteredByEntityType(
      getNotesFilteredByArchived(notes, !isArchivedFilterOn),
      getCurrentEntityFilter(filtersOn),
    );
    setFilteredNotes(newNotes);
  };

  /**
   * Filters notes by archived status
   *
   * @param currentNotes - Current notes to filter
   * @param displayArchivedNotes - Boolean flag to determine which notes to show
   * @returns Filtered notes array based on archived status
   */
  function getNotesFilteredByArchived(
    currentNotes: NoteManyQuery["noteMany"],
    displayArchivedNotes: boolean,
  ) {
    return displayArchivedNotes
      ? currentNotes.filter(n => n.isArchived) || []
      : currentNotes.filter(n => !n.isArchived) || [];
  }

  /**
   * Toggles the "my notes" filter on/off
   *
   * Logic:
   * - When enabling myNotes filter, automatically removes "archived" filter (mutually exclusive)
   * - Shows notes authored by current user OR notes that mention the current user
   * - Always applies the entity filters & returns only non-archived notes (business rule)
   * - Clears search to avoid confusion with filtered results
   */
  const handleToggleMyNotes = () => {
    setSearch("");
    const isMyNotesFilterOn = filtersOn.includes("myNotes");

    setFiltersOn(
      prev =>
        isMyNotesFilterOn
          ? prev.filter(f => f !== "myNotes")
          : [...prev.filter(f => f !== "archived"), "myNotes"], // When adding myNotes filter, remove archived filter
    );

    const newNotes = getNotesFilteredByEntityType(
      getNotesFilteredByMyNotes(notes, !isMyNotesFilterOn).filter(
        n => !n.isArchived,
      ),
      getCurrentEntityFilter(filtersOn),
    );
    setFilteredNotes(newNotes);
  };

  /**
   * Filters notes to show only user's own notes or notes mentioning the user
   *
   * Logic:
   * - Returns notes where current user is the author OR current user is mentioned
   * - Uses dbUser._id to match against note.author._id and note.mentions array
   * - If filter is off, returns all notes unchanged
   *
   * @param currentNotes - Current notes to filter
   * @param displayMyNotes - Boolean flag to determine if filtering should be applied
   * @returns Filtered notes array based on user ownership/mentions
   */
  function getNotesFilteredByMyNotes(
    currentNotes: NoteManyQuery["noteMany"],
    displayMyNotes: boolean,
  ) {
    return displayMyNotes
      ? currentNotes.filter(
          n =>
            n.author?._id === dbUser?._id ||
            n.mentions.map(m => m._id).includes(dbUser?._id), // Return notes that are either authored by the user or mentions the user
        ) || []
      : currentNotes || [];
  }

  /**
   * Toggles entity type filters (people, operators, lists, widgets)
   *
   * Logic:
   * - Entity filters are mutually exclusive - only one can be active at a time
   * - When enabling an entity filter, removes all other entity filters first
   * - Maintains archived and myNotes filter states
   * - Clears search to avoid confusion with filtered results
   * - Updates both filter state and filtered notes immediately
   *
   * @param entityTypeFilter - The specific entity type to toggle
   */
  const handleToggleEntityFilter = (
    entityTypeFilter: "people" | "operators" | "lists" | "widgets",
  ) => {
    setSearch("");
    const isEntityFilterOn = filtersOn.includes(entityTypeFilter);
    const isArchivedFilterOn = filtersOn.includes("archived");
    const isMyNotesFilterOn = filtersOn.includes("myNotes");
    const otherEntityFilters = ALL_ENTITY_FILTERS.filter(
      e => e !== entityTypeFilter,
    );

    setFiltersOn(prev =>
      isEntityFilterOn
        ? prev.filter(f => f !== entityTypeFilter)
        : [
            ...prev.filter(f => !otherEntityFilters.includes(f)), // When adding an entity filter, remove the other entity filters
            entityTypeFilter,
          ],
    );

    const newFilteredNotes = getNotesFilteredByMyNotes(
      getNotesFilteredByArchived(
        getNotesFilteredByEntityType(
          notes,
          entityTypeFilter,
          !isEntityFilterOn,
        ),
        isArchivedFilterOn,
      ),
      isMyNotesFilterOn,
    );

    setFilteredNotes(newFilteredNotes);
  };

  /**
   * Filters notes by entity type (people, operators, lists, widgets)
   *
   * Logic:
   * - Uses linkedToEntities to determine the actual entity type of each note
   * - Matches note.entity._entityId with linkedToEntities.entityId
   * - Filters based on the entityType field from linkedToEntities
   * - If no entity filter is specified or filterByEntityType is false, returns all notes
   *
   * @param currentNotes - Current notes to filter
   * @param entityTypeFilter - The entity type to filter by (optional)
   * @param filterByEntityType - Boolean flag to determine if filtering should be applied
   * @returns Filtered notes array based on entity type
   */
  function getNotesFilteredByEntityType(
    currentNotes: NoteManyQuery["noteMany"],
    entityTypeFilter?: "people" | "operators" | "lists" | "widgets",
    filterByEntityType: boolean = true,
  ) {
    if (!entityTypeFilter || !filterByEntityType) return currentNotes;

    const newFilteredNotes = currentNotes.filter(note => {
      const currentEntity = linkedToEntities.find(
        e => e.entityId === note.entity._entityId,
      );
      if (!currentEntity) return false;

      switch (entityTypeFilter) {
        case "people":
          return currentEntity.entityType === EnumEntityReferenceType.Person;
        case "operators":
          return currentEntity.entityType === EnumEntityReferenceType.Operator;
        case "lists":
          return currentEntity.entityType === EnumEntityReferenceType.List;
        case "widgets":
          return currentEntity.entityType === EnumEntityReferenceType.Widget;
        default:
          return false;
      }
    });

    return newFilteredNotes;
  }

  // Defer so create/save/edit/delete don’t block: grouping runs in a later render
  const deferredFilteredNotes = useDeferredValue(filteredNotes);

  /**
   * Groups filtered notes by date for timeline display
   *
   * Logic:
   * - Maps each note to include a groupByDate field
   * - Uses noteDateISO if available, falls back to createdDateISO
   * - Orders groups by date in descending order (newest first)
   * - Returns array of groups with groupByDate and items properties
   */
  const filteredNotesGroupedByDate: NoteTimelineGroup[] = useMemo(() => {
    const mappedNotesByDate = _.groupBy(
      deferredFilteredNotes.map(note => {
        const groupByDate = note.noteDateISO
          ? format(toZonedTime(note.noteDateISO, "UTC"), DATE_PATTERN_SIMPLE)
          : format(note.createdDateISO ?? "", DATE_PATTERN_SIMPLE);
        return {
          _id: note._id,
          groupByDate,
          note,
        };
      }),
      "groupByDate",
    );
    const groupedNotesByDateArray = Object.values(mappedNotesByDate).map(
      group => ({
        groupByDate: group[0].groupByDate,
        items: group,
      }),
    );
    return _.orderBy(groupedNotesByDateArray, "groupByDate", "desc");
  }, [deferredFilteredNotes]);

  const handleNewNoteSaveSuccess = async () => {
    setIsAddingNote(false);
  };

  const currentEntityFilter = useMemo(
    () => getCurrentEntityFilter(filtersOn),
    [filtersOn],
  );

  return {
    filteredNotesGroupedByDate,
    loading: initialLoad || loadingLinkedToEntities,
    search,
    onSearch: handleSearch,
    onToggleArchivedNotes: handleToggleArchivedNotes,
    onToggleMyNotes: handleToggleMyNotes,
    onToggleEntityFilter: handleToggleEntityFilter,
    onNewNoteSaveSuccess: handleNewNoteSaveSuccess,
    filtersOn,
    currentEntityFilter,
    isAddingNote,
    setIsAddingNote,
  };
};

function getCurrentEntityFilter(filtersOn: Filter[]) {
  return filtersOn.find(f => ALL_ENTITY_FILTERS.includes(f)) as
    | "people"
    | "operators"
    | "lists"
    | "widgets";
}
